import {
    ConfirmPaymentResponse,
    ExchangeSessionResponse,
    PaymentApiErrorBody,
    PaymentOrderView,
} from '../types';

const configuredPaymentApiBase = process.env.REACT_APP_GIFT_PAYMENT_API_BASE?.replace(/\/$/, '');
const PAYMENT_API_BASE = configuredPaymentApiBase || '/v1/gifticon/payment';
const paymentRequestHeaders = (headers: Record<string, string> = {}): Record<string, string> => ({
    ...headers,
    'ngrok-skip-browser-warning': '1',
});

export class PaymentApiError extends Error {
    readonly status: number;
    readonly errorCode?: string;
    readonly details?: Record<string, unknown>;

    constructor(status: number, body?: Partial<PaymentApiErrorBody>) {
        super(body?.message || '결제 정보를 불러오지 못했어요.');
        this.name = 'PaymentApiError';
        this.status = status;
        this.errorCode = body?.errorCode;
        this.details = body?.details;
    }
}

const parseResponse = async <T>(response: Response): Promise<T> => {
    let body: unknown;
    try {
        body = await response.json();
    } catch {
        body = undefined;
    }

    if (!response.ok) {
        const errorBody =
            body && typeof body === 'object' ? (body as Partial<PaymentApiErrorBody>) : undefined;
        throw new PaymentApiError(response.status, errorBody);
    }

    return body as T;
};

export const exchangeGiftPaymentSession = async (
    orderId: string,
    sessionToken: string,
): Promise<ExchangeSessionResponse> => {
    const response = await fetch(`${PAYMENT_API_BASE}/sessions/exchange`, {
        method: 'POST',
        credentials: 'include',
        headers: paymentRequestHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ orderId, sessionToken }),
    });
    return parseResponse<ExchangeSessionResponse>(response);
};

export const fetchGiftPaymentOrder = async (orderId: string): Promise<PaymentOrderView> => {
    const response = await fetch(`${PAYMENT_API_BASE}/orders/${encodeURIComponent(orderId)}`, {
        credentials: 'include',
        headers: paymentRequestHeaders(),
    });
    return parseResponse<PaymentOrderView>(response);
};

export const confirmGiftPayment = async (
    orderId: string,
    paymentKey: string,
    amount: number,
    csrfToken: string,
): Promise<ConfirmPaymentResponse> => {
    const response = await fetch(
        `${PAYMENT_API_BASE}/orders/${encodeURIComponent(orderId)}/confirm`,
        {
            method: 'POST',
            credentials: 'include',
            headers: paymentRequestHeaders({
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken,
            }),
            body: JSON.stringify({ orderId, paymentKey, amount }),
        },
    );
    return parseResponse<ConfirmPaymentResponse>(response);
};
